import { useEffect, useRef } from "react";

// Open modals, bottom to top. Escape should only close the topmost one.
const modalStack: symbol[] = [];

interface ModalProps {
  onClose: () => void;
  children: React.ReactNode;
  /** Accessible label for the dialog. */
  ariaLabel?: string;
  /** Panel max width. Defaults to "md". */
  size?: "md" | "xl" | "2xl";
  /**
   * Cap the panel to the viewport height and make it a flex column so its own
   * children can scroll internally (instead of the overlay scrolling).
   */
  fitHeight?: boolean;
}

/**
 * A centered overlay dialog. Closes on backdrop click or Escape. Locks body
 * scroll while open and restores focus to the previously-focused element on
 * unmount.
 */
export function Modal({
  onClose,
  children,
  ariaLabel,
  size = "md",
  fitHeight = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Callers pass inline closures, so `onClose` changes identity on every
  // render. Read it through a ref: if it were an effect dep, any re-render
  // (e.g. a board poll landing) would re-run the effect, whose cleanup hands
  // focus back to the board and yanks the caret out of whatever field the
  // user is typing in.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const token = Symbol("modal");
    modalStack.push(token);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // move focus into the dialog — unless a child already claimed it (child
    // effects run first, e.g. a title opening in edit mode)
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (modalStack[modalStack.length - 1] !== token) return;
      // Escape while typing in a field belongs to that field (cancel an edit,
      // dismiss a draft) — it must not also close the whole dialog.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.stopPropagation();
      onCloseRef.current();
    }
    document.addEventListener("keydown", onKeyDown);

    return () => {
      const i = modalStack.indexOf(token);
      if (i >= 0) modalStack.splice(i, 1);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const width =
    size === "2xl" ? "max-w-6xl" : size === "xl" ? "max-w-5xl" : "max-w-2xl";
  const panelSizing = fitHeight
    ? "flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden"
    : "my-4";

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/50 p-4 sm:p-6 ${
        fitHeight ? "items-start sm:items-center" : "items-start"
      }`}
      onMouseDown={(e) => {
        // only close when the backdrop itself is pressed (not a child)
        if (e.target === e.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={`w-full ${width} rounded-xl bg-[var(--surface)] shadow-xl outline-none ${panelSizing}`}
      >
        {children}
      </div>
    </div>
  );
}
